import React, { useState, useEffect, useRef } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth } from '../../firebase';
import { FaTimes, FaKey, FaArrowLeft } from 'react-icons/fa';
import { getEmailNotificationPreference, setEmailNotificationPreference } from '../../services/userService';

const inputClass =
  'block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm';

// Read-only labelled value; renders a dash when empty.
const Field = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900 break-words">{value === 0 || value ? value : '—'}</dd>
  </div>
);

// Grouped block of read-only fields, mirroring the admin franchisee-details sections.
const Section = ({ title, children }) => (
  <div className="border-t border-gray-200 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">{children}</dl>
  </div>
);

const AccountModal = ({ franchisee, onClose }) => {
  const [view, setView] = useState('profile'); // 'profile' | 'password'

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  // Assume on until the backend says otherwise, matching the server's own default for a franchisee
  // who has never touched the setting.
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [notifyLoading, setNotifyLoading] = useState(true);
  const [notifyError, setNotifyError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getEmailNotificationPreference()
      .then((data) => { if (!cancelled) setNotifyEnabled(data.emailNotificationsEnabled !== false); })
      .catch((err) => { if (!cancelled) setNotifyError(err.message); })
      .finally(() => { if (!cancelled) setNotifyLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleToggleNotifications = async () => {
    const next = !notifyEnabled;
    setNotifyEnabled(next); // optimistic, reverted below if the save fails
    setNotifyLoading(true);
    setNotifyError('');
    try {
      await setEmailNotificationPreference(next);
    } catch (err) {
      setNotifyEnabled(!next);
      setNotifyError(err.message);
    } finally {
      setNotifyLoading(false);
    }
  };

  const cardRef = useRef(null);
  const mouseDownOnBackdrop = useRef(false);
  // Keep the latest onClose without re-running the mount-only effect below.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Dialog behaviour: focus the card on open, trap Tab, close on Escape, restore focus on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    cardRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && cardRef.current) {
        const focusable = cardRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, []);

  const yesNo = (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : '—');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (newPassword.length < 6) {
      setPwError('New password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }

    const user = auth.currentUser;
    if (!user || !user.email) {
      setPwError('You must be logged in to change your password.');
      return;
    }

    setSaving(true);
    try {
      // Reauthenticate with the current password before updating (voluntary change).
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPwSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPwError('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password' || err.code === 'auth/password-does-not-meet-requirements') {
        setPwError('New password does not meet the required password policy. Please choose a stronger password.');
      } else if (err.code === 'auth/requires-recent-login') {
        setPwError('For security reasons, please log out and log back in, then try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setPwError('Too many attempts. Please try again later.');
      } else {
        setPwError('Failed to change password. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const openPasswordView = () => {
    setPwError('');
    setPwSuccess('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setView('password');
  };

  const handleBackdropMouseDown = (e) => {
    // Only a press that starts on the backdrop itself may dismiss (avoids drag-release closing).
    mouseDownOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && mouseDownOnBackdrop.current) {
      onClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  const renderProfile = () => {
    if (!franchisee) {
      return <p className="text-sm text-gray-600">Unable to load your account details.</p>;
    }
    return (
      <>
        <Section title="Business">
          <Field label="Franchise Code" value={franchisee.fseCode} />
          <Field label="Franchise Name" value={franchisee.franchiseName} />
          <Field label="External Franchisee" value={yesNo(franchisee.isExternal)} />
        </Section>

        <Section title="Personal">
          <Field label="First Name" value={franchisee.firstName} />
          <Field label="Last Name" value={franchisee.lastName} />
          <Field label="Email" value={franchisee.email} />
          <Field label="Phone Number" value={franchisee.phoneNumber} />
        </Section>

        <Section title="Tax">
          <Field label="ABN" value={franchisee.abn} />
          <Field label="GST Registered" value={yesNo(franchisee.gstRegistered)} />
        </Section>

        <Section title="Bank Details">
          <Field label="BSB Number" value={franchisee.bsbNumber} />
          <Field label="Bank Account Number" value={franchisee.bankAccountNumber} />
          <Field label="Bank Account Name" value={franchisee.bankAccountName} />
        </Section>

        <Section title="Address">
          <Field label="Territory" value={franchisee.territory} />
          <Field label="Address Line 1" value={franchisee.addressLine1} />
          <Field label="Address Line 2" value={franchisee.addressLine2} />
          <Field label="State" value={franchisee.state} />
          <Field label="Postal Code" value={franchisee.postalCode} />
          <Field label="Country" value={franchisee.country} />
        </Section>

        <Section title="Email Notifications">
          <div className="flex items-start justify-between gap-4 py-1">
            <div>
              <p className="text-sm text-gray-800">Receive reminder and report emails</p>
              <p className="text-xs text-gray-500 mt-1">
                Turning this off stops job reminders, overdue chasers and your weekly report.
                Your RCTIs and account emails are always sent.
              </p>
              {notifyError && <p className="text-xs text-red-600 mt-1">{notifyError}</p>}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={notifyEnabled}
              aria-label="Receive reminder and report emails"
              disabled={notifyLoading}
              onClick={handleToggleNotifications}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 ${notifyEnabled ? 'bg-green-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${notifyEnabled ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
        </Section>

        <div className="mt-6">
          <button
            type="button"
            onClick={openPasswordView}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <FaKey /> Change Password
          </button>
        </div>
      </>
    );
  };

  const renderPasswordForm = () => (
    <form onSubmit={handleChangePassword} className="space-y-4">
      <div>
        <label htmlFor="currentPassword" className="block text-xs font-medium text-gray-500">
          Current Password
        </label>
        <input
          id="currentPassword"
          type={showPasswords ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
          className={inputClass}
          placeholder="Enter current password"
        />
      </div>
      <div>
        <label htmlFor="newPassword" className="block text-xs font-medium text-gray-500">
          New Password
        </label>
        <input
          id="newPassword"
          type={showPasswords ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
          className={inputClass}
          placeholder="Enter new password"
        />
      </div>
      <div>
        <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-500">
          Confirm New Password
        </label>
        <input
          id="confirmPassword"
          type={showPasswords ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
          className={inputClass}
          placeholder="Confirm new password"
        />
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-600">
        <input type="checkbox" checked={showPasswords} onChange={() => setShowPasswords(!showPasswords)} />
        Show passwords
      </label>

      {pwError && <p className="text-sm text-red-600">{pwError}</p>}
      {pwSuccess && <p className="text-sm text-green-600">{pwSuccess}</p>}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => setView('profile')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <FaArrowLeft /> Back
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Update Password'}
        </button>
      </div>
    </form>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="account-modal-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 id="account-modal-title" className="text-lg font-bold text-gray-900">
            {view === 'profile' ? 'Account' : 'Change Password'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <FaTimes />
          </button>
        </div>
        <div className="px-6 py-4">{view === 'profile' ? renderProfile() : renderPasswordForm()}</div>
      </div>
    </div>
  );
};

export default AccountModal;
